import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spin } from 'antd';
import CommonForm from '@/components/CommonForm';
import { FormInstance, message } from 'antd';
import {
  addGlobalEventListener,
  removeGlobalEventListener,
} from '@/utils/emit';
import { GlobalEventName } from '@/utils/constant';
import { HttpStatusCode } from '@/fetch/core/constant';
import { accidentApi } from '@/fetch/bussiness/accident';
import { formConfig, ValidationMap, ValidationCtx } from './constants';
import UseGetCommonEnumMap from '@/utils/hooks/UseGetCommonEnumMap';
import { CommonEnumKey } from '@/utils/enum';

interface AccidentOrderAddModalProps {
  // 车号
  vehicleName: string;
  // 工单编号，用于事故单和工单关联（只有从工单抽屉提报的事故单才需要此字段，即才需要建立关联关系，从单车页提报的不需要）
  issueNumber?: string;
  // 是否显示弹窗
  visible?: boolean;
  // 关闭弹窗的回调
  onCancel: () => void;
}

const AccidentOrderAddModal = ({
  vehicleName,
  issueNumber,
  visible = true,
  onCancel,
}: AccidentOrderAddModalProps) => {
  const tsaList = useRef<any>([]); // 凭证文件列表
  const formInstance = useRef<any>(null); // 表单实例
  const fileUploading = useRef<boolean>(false); // 文件是否正在上传中的loading态
  const [loading, setLoading] = useState<boolean>(false); // 提交事故单的loading态
  const { enumMap, loadingEnumMap } = UseGetCommonEnumMap([
    CommonEnumKey.NEW_ACCIDENT_LEVEL_ENUM,
    CommonEnumKey.ACCIDENT_RESPONSIBLE_ENUM,
  ]); // 公共枚举值（“事故等级”和“有无责任”的下拉框数据）
  const [editFormConfig, setEditFormConfig] = useState(formConfig); // 表单配置

  /**
   * 页面挂载--拉取公共枚举值，并给表单加上枚举数据
   */
  useEffect(() => {
    const newFormConfig = { ...formConfig };
    const responsibleField = newFormConfig.data.find(
      (item: any) => item.fieldName === 'responsible'
    );
    const accidentLevelField = newFormConfig.data.find(
      (item: any) => item.fieldName === 'technicalSupportAccidentLevel'
    );
    if (responsibleField)
      responsibleField.data = enumMap[CommonEnumKey.ACCIDENT_RESPONSIBLE_ENUM];
    if (accidentLevelField)
      accidentLevelField.data = enumMap[CommonEnumKey.NEW_ACCIDENT_LEVEL_ENUM];

    setEditFormConfig(newFormConfig);
  }, [enumMap]);

  /**
   * 页面挂载--建立监听器，监听文件上传状态
   */
  useEffect(() => {
    addGlobalEventListener(
      GlobalEventName.JDX_FILE_UPLOAD_STATUS,
      onListenFileUploadStatus
    );
    return () => {
      removeGlobalEventListener(
        GlobalEventName.JDX_FILE_UPLOAD_STATUS,
        onListenFileUploadStatus
      );
    };
  }, []);

  /**
   * 当监听到文件正在上传，则将fileUploading置为true
   * @param {*} res
   */
  const onListenFileUploadStatus = (res: any) => {
    if ('technicalSupportAttmentList' === res.fieldName) {
      fileUploading.current = res.uploading;
    }
  };

  /**
   * 文件上传成功回调，把文件信息存到tsaList中
   * @param {string} fieldName
   * @param {*} fileObj
   * @param {string} type
   */
  const onFieldChange = (fieldName: string, fileObj: any, type: string) => {
    if ('technicalSupportAttmentList' === fieldName) {
      const fileList = tsaList.current.filter((item: any) => {
        return item.fileKey === fileObj.fileKey || item.uid === fileObj.uid;
      });
      if ('add' === type) {
        if (fileList.length === 0) {
          tsaList.current.push(fileObj);
        }
      } else if ('delete' === type) {
        fileList.map((item: any) => {
          tsaList.current.splice(tsaList.current.indexOf(item), 1);
        });
      }
    }
  };

  /**
   * 提交前校验事故单字段
   * @return {*}
   */
  const handleValidateBeforeSubmit = () => {
    // 构造校验上下文
    const ctx: ValidationCtx = {
      fileUploading: fileUploading.current,
      vehicleName,
      formInstance: formInstance.current,
    };
    // 校验
    const error = ValidationMap.find((item) => item.when(ctx));
    if (!error) return true;
    // 校验不通过提示错误信息
    message.error(error.message || '请完善表单信息！');
    return false;
  };

  /**
   * 提交事故单
   * @return {*}
   */
  const handleSubmit = async () => {
    const isValidate = handleValidateBeforeSubmit();
    if (!isValidate) return;
    try {
      setLoading(true);
      const fieldsValue = await formInstance.current.validateFields();
      const saveValue: any = {
        technicalSupportDescription: fieldsValue.technicalSupportDescription,
        technicalSupportAccidentLevel:
          fieldsValue.technicalSupportAccidentLevel,
        responsible: fieldsValue.responsible,
        vehicleName,
        accidentAddress: fieldsValue.accidentAddress,
        accidentTime: fieldsValue.accidentTime.format('YYYY-MM-DD HH:mm:ss'),
        technicalSupportAttmentList:
          tsaList.current?.length > 0 ? tsaList.current : null,
        issueNumber: issueNumber || null,
      };
      const res = await accidentApi.manualCreateAccident(saveValue);
      if (res && res.code === HttpStatusCode.Success) {
        message.success('创建成功！');
        onCancel && onCancel();
      } else {
        throw new Error(res?.message || '创建失败！');
      }
    } catch (error: any) {
      message.error(error?.message || '请完善表单信息！');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 渲染底部按钮
   */
  const renderModalFooter = () => (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
      <Button onClick={onCancel}>取消</Button>
      <Button onClick={handleSubmit} loading={loading} type="primary">
        确定
      </Button>
    </div>
  );

  return (
    <Modal
      title="创建事故"
      onCancel={onCancel}
      width={1200}
      open={visible}
      footer={renderModalFooter()}
    >
      <Spin spinning={loadingEnumMap}>
        <CommonForm
          className="accident-form"
          config={editFormConfig}
          layout="horizontal"
          getFormInstance={(formRef: FormInstance) => {
            formInstance.current = formRef;
          }}
          defaultValue={{
            isKnown: 0,
          }}
          onFieldChange={onFieldChange}
        />
      </Spin>
    </Modal>
  );
};

export default AccidentOrderAddModal;
